/**
 * Persistent, dismissible nudge back to the /app/welcome setup checklist.
 *
 * The welcome flow only auto-shows pre-org (RequireAuth routes there when the
 * user has no org). Once an org exists the checklist (code source, credential,
 * first session) can still be incomplete with no way back — so this banner
 * sits in the app shell and links to it while any tracked step is unfinished
 * and the org admin hasn't dismissed it (dismissal persists per org).
 *
 * Not in the admin area: there the reminder is an item in the admin menu
 * (sidebar), where it no longer covers the page it is about.
 *
 * From md the shell places it above the panels, on their outer edges (the
 * left column's left edge to the right column's right edge). On a phone it
 * sits at the top of the centre panel and scrolls away with the page.
 */
import * as React from "react";
import { Box, Button, IconButton, Typography, type SxProps, type Theme } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Close as CloseIcon } from "@/components/icons";
import { isAdminPath } from "@/features/admin/nav/adminPaths";
import { repoTokens } from "@/theme/tokens";

import { useSetupNudge } from "./useSetupNudge";

const WELCOME_PATH = "/app/welcome";

export function OnboardingNudge({
  inPanel = false,
  sx,
}: {
  /** Inside a panel (phones): the elevated surface, so it reads against
   *  the panel's own colour. Otherwise a panel on the page. */
  inPanel?: boolean;
  sx?: SxProps<Theme>;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const location = useLocation();
  // Shared with the top-bar Setup icon: dismissing here hides both.
  const nudge = useSetupNudge();

  if (!nudge.visible || location.pathname === WELCOME_PATH || isAdminPath(location.pathname)) {
    return null;
  }

  return (
    <Box
      role="status"
      data-testid="setup-nudge"
      sx={[
        {
          // The panel look (#1A1A1A, 14px radius, no border) with a thin
          // warning rule on the left instead of a full yellow outline.
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 1.5,
          px: 2,
          bgcolor: inPanel ? repoTokens.colors.elevated : repoTokens.colors.panel,
          borderRadius: `${repoTokens.shell.panelRadius}px`,
          flexShrink: 0,
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 10,
            bottom: 10,
            width: 3,
            borderRadius: "0 2px 2px 0",
            bgcolor: repoTokens.colors.warning,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        {t("onboarding.nudge", {
          count: nudge.incompleteCount,
          defaultValue_one: "Finish setting up your workspace — 1 step left.",
          defaultValue_other:
            "Finish setting up your workspace — {{count}} steps left.",
        })}
      </Typography>
      <Button
        color="secondary"
        size="small"
        component={RouterLink}
        to={WELCOME_PATH}
        // keep-all: break only between words, so a CJK label
        // ("設定を完了") is not split one glyph per line on a phone.
        // Spaced languages still wrap at their spaces.
        sx={[
          { wordBreak: "keep-all", flexShrink: 0 },
          // 24px high from md, so the banner is 48px with its 12px
          // padding: it sits above the start page's grid, which has to
          // fit a 1360x900 window. Phones keep the larger touch target.
          !inPanel && { py: 0.25, lineHeight: "20px" },
        ]}
      >
        {t("onboarding.nudge_cta", "Finish setup")}
      </Button>
      <IconButton
        color="inherit"
        size="small"
        aria-label={t("mui.alert.close")}
        onClick={nudge.dismiss}
        sx={[{ flexShrink: 0 }, !inPanel && { p: 0.25 }]}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
